import * as Phaser from 'phaser';
import type { PlayerState, ShipId, ShipDefinition, InputMessage } from '@rttv/shared';
import { SHIP_DEFINITIONS, SHIELD_RADIUS_BONUS, WORLD_WIDTH, WORLD_HEIGHT, BORDER_THICKNESS, getRankMult } from '@rttv/shared';
import { uiState } from '../uiState';
import { getShipVisual } from '../shipVisuals';
import type { ShipNozzle } from '../shipVisuals';

// Local player: dead reckoning + time-based lerp for server correction.
const LERP_K = 13.4;

// Remote players: render INTERP_DELAY_MS behind real-time, interpolating between
// buffered server snapshots. Absorbs network jitter without any lerp corrections.
const INTERP_DELAY_MS = 100; // 6 intervals at 60Hz patches — absorbs real network jitter
const MAX_SNAPSHOTS   = 30;  // ~500ms of history at 60Hz

interface StateSnapshot {
  time:     number;
  x:        number;
  y:        number;
  rotation: number;
  velX:     number;
  velY:     number;
}

const DEBRIS_FRAMES = [
  'meteorBrown_tiny1.png',
  'meteorBrown_tiny2.png',
  'meteorGrey_tiny1.png',
  'meteorGrey_tiny2.png',
  'meteorBrown_small1.png',
  'meteorGrey_small1.png',
];

export class ShipSprite {
  public  image:  Phaser.GameObjects.Image;
  public  isDead = false;

  private scene:            Phaser.Scene;
  private shieldGfx:        Phaser.GameObjects.Graphics;
  private shieldRadius:     number;
  private shieldColor:      number;
  private shipRadius:       number;
  private shieldPct         = 1;

  // Ship physics def — needed for client-side prediction
  private def: ShipDefinition;

  // Local player: dead-reckoning fallback fields (used if pred state uninitialised)
  private targetX: number;
  private targetY: number;
  private targetRotation: number;
  private velX = 0;
  private velY = 0;

  // Local player: client-side predicted state
  private predX      = 0;
  private predY      = 0;
  private predVelX   = 0;
  private predVelY   = 0;
  private predRot    = 0;
  private predEnergy = 0;
  private predReady  = false; // true once initialised from first server state

  // Remote player jitter buffer
  private snapshots:   StateSnapshot[] = [];
  private lastSnapMs = 0; // deduplicate: x/y/rotation/isThrusting all call onServerUpdate

  private nozzles:       readonly ShipNozzle[] = [];
  private flameTime      = 0;
  private isThrustingNow = false;
  /** Pulsing cockpit dome + headlight beams for the UFO. Null on other ships. */
  private noseLightGfx:  Phaser.GameObjects.Graphics | null = null;
  private shipId: string = 'fighter';

  // Enemy overlay
  private isLocal      = false;
  private armorPct     = 1;
  private maxArmor     = 100;
  private maxShield    = 60;
  private currentShield = 0;
  private overlayGfx!: Phaser.GameObjects.Graphics;
  private overlayName!: Phaser.GameObjects.Text;
  private displayName  = '';
  private rankIndex    = 0;

  constructor(scene: Phaser.Scene, state: PlayerState, isLocal = false) {
    this.scene   = scene;
    this.isLocal = isLocal;

    const def    = SHIP_DEFINITIONS[state.shipId as ShipId] ?? SHIP_DEFINITIONS.fighter;
    const visual = getShipVisual(def.id);
    this.def    = def;
    this.shipId = def.id;
    this.image = visual.frame === undefined
      ? scene.add.image(state.x, state.y, visual.textureKey)
      : scene.add.image(state.x, state.y, visual.textureKey, visual.frame);
    this.image.setRotation(state.rotation);
    this.image.setDepth(10);
    this.image.setScale(visual.scale * (visual.widthScale ?? 1), visual.scale);

    this.targetX        = state.x;
    this.targetY        = state.y;
    this.targetRotation = state.rotation;

    this.shipRadius   = def.radius;
    this.shieldRadius = def.radius + SHIELD_RADIUS_BONUS;
    this.shieldColor  = def.shieldColor;
    this.rankIndex    = state.rankIndex ?? 0;
    this.maxShield    = def.shield * getRankMult(this.rankIndex);
    this.currentShield = state.shield ?? this.maxShield;
    this.shieldPct    = this.maxShield > 0 ? Math.min(1, this.currentShield / this.maxShield) : 0;
    this.maxArmor  = def.armor;
    this.armorPct  = def.armor > 0 ? Math.min(1, (state.armor ?? def.armor) / def.armor) : 0;

    this.shieldGfx = scene.add.graphics();
    this.shieldGfx.setDepth(11);

    this.nozzles = visual.nozzles;

    // UFO: cockpit dome glow + headlight beams so the nose direction is readable
    // Wraith: ghost aura at nose tip — faint green halo so the orientation is visible
    if (visual.noseFx) {
      this.noseLightGfx = scene.add.graphics();
      this.noseLightGfx.setDepth(12);
    }

    this.displayName = state.displayName;

    // Enemy overlay — drawn via Graphics each frame (avoids Phaser 4 Rectangle.setSize quirks)
    this.overlayGfx  = scene.add.graphics().setDepth(13);
    this.overlayName = scene.add.text(state.x, state.y - 61, this.buildNameText(), {
      fontSize: '12px', fontFamily: 'KenvectorFutureThin', color: '#ffffff',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5, 0.5).setDepth(14).setVisible(false);
  }

  setThrusting(v: boolean) { this.isThrustingNow = v; }

  private buildNameText(): string {
    const pips = '★'.repeat(this.rankIndex + 1);
    return `${pips} ${this.displayName}`;
  }

  setRankIndex(idx: number) {
    this.rankIndex = idx;
    this.maxShield = this.def.shield * getRankMult(idx);
    this.shieldPct = this.maxShield > 0 ? Math.max(0, Math.min(1, this.currentShield / this.maxShield)) : 0;
    this.overlayName.setText(this.buildNameText());
  }

  onServerUpdate(state: PlayerState) {
    this.isThrustingNow = state.isThrusting ?? false;
    if (this.isLocal) {
      this.reconcileWithServer(state);
    } else {
      const now = Date.now();
      if (now - this.lastSnapMs > 25) {
        this.snapshots.push({ time: now, x: state.x, y: state.y, rotation: state.rotation,
          velX: state.velocityX, velY: state.velocityY });
        this.lastSnapMs = now;
        if (this.snapshots.length > MAX_SNAPSHOTS) this.snapshots.shift();
      }
    }
  }

  /** Called from GameScene.update() each frame — advances predicted position using the
   *  latest local input before the server confirms it. */
  simulateInput(input: InputMessage, dt: number) {
    if (!this.isLocal || this.isDead) return;

    // Rotation
    if (input.rotateLeft)  this.predRot -= this.def.rotationSpeed * dt;
    if (input.rotateRight) this.predRot += this.def.rotationSpeed * dt;
    this.predRot = Math.atan2(Math.sin(this.predRot), Math.cos(this.predRot));

    // Energy recharge
    this.predEnergy = Math.min(this.def.energy, this.predEnergy + this.def.recharge * dt);

    // Thrust
    const thrusting = input.thrust && this.predEnergy > 0;
    if (thrusting) {
      this.predVelX += Math.sin(this.predRot) * this.def.thrust * dt;
      this.predVelY -= Math.cos(this.predRot) * this.def.thrust * dt;
      this.predEnergy = Math.max(0, this.predEnergy - this.def.thrustDrain * dt);
    }

    // Brake
    if (input.brake && this.predEnergy > 0) {
      this.predVelX -= Math.sin(this.predRot) * this.def.thrust / 3 * dt;
      this.predVelY += Math.cos(this.predRot) * this.def.thrust / 3 * dt;
      this.predEnergy = Math.max(0, this.predEnergy - this.def.thrustDrain / 3 * dt);
    }

    // Per-hull damping (currently the Skimmer)
    if (this.def.hasDamping) {
      this.predVelX *= 1 - 1.5 * dt;
      this.predVelY *= 1 - 1.5 * dt;
    }

    // Speed cap
    const spd = Math.hypot(this.predVelX, this.predVelY);
    if (spd > this.def.speed) {
      const scale = this.def.speed / spd;
      this.predVelX *= scale;
      this.predVelY *= scale;
    }

    // Position integration
    this.predX += this.predVelX * dt;
    this.predY += this.predVelY * dt;

    // Soft world bounds — don't predict wall bounce physics, let server correct
    this.predX = Math.max(BORDER_THICKNESS, Math.min(WORLD_WIDTH  - BORDER_THICKNESS, this.predX));
    this.predY = Math.max(BORDER_THICKNESS, Math.min(WORLD_HEIGHT - BORDER_THICKNESS, this.predY));
  }

  private reconcileWithServer(state: PlayerState) {
    const errX = state.x - this.predX;
    const errY = state.y - this.predY;

    if (!this.predReady || Math.hypot(errX, errY) > 200) {
      // First update or large teleport (respawn, lag spike) — snap immediately
      this.predX      = state.x;
      this.predY      = state.y;
      this.predVelX   = state.velocityX;
      this.predVelY   = state.velocityY;
      this.predRot    = state.rotation;
      this.predEnergy = state.energy;
      this.predReady  = true;
      return;
    }

    // Blend toward server each patch. At 60Hz patches, 0.1 gives the same convergence
    // rate as 0.3 did at 20Hz (≈27% per 50ms vs 30% per 50ms).
    const a = 0.1;
    this.predX    += errX * a;
    this.predY    += errY * a;
    this.predVelX += (state.velocityX - this.predVelX) * a;
    this.predVelY += (state.velocityY - this.predVelY) * a;

    // Rotation is deterministic from inputs — just accept server value
    let rotDiff = state.rotation - this.predRot;
    if (rotDiff >  Math.PI) rotDiff -= Math.PI * 2;
    if (rotDiff < -Math.PI) rotDiff += Math.PI * 2;
    this.predRot += rotDiff * a;

    // Energy: snap silently (it's not visual)
    this.predEnergy = state.energy;
  }

  onShieldUpdate(state: PlayerState) {
    this.currentShield = state.shield;
    this.shieldPct = this.maxShield > 0 ? Math.max(0, Math.min(1, state.shield / this.maxShield)) : 0;
  }

  onArmorUpdate(state: PlayerState) {
    this.armorPct = this.maxArmor > 0 ? Math.max(0, Math.min(1, state.armor / this.maxArmor)) : 0;
  }

  /**
   * Directional shield hit flash (Star Trek style).
   * impactAngle points FROM target TOWARD attacker — the struck face.
   */
  onShieldHit(impactAngle: number) {
    const cx     = this.image.x;
    const cy     = this.image.y;
    const r      = this.shieldRadius;
    const spread = Math.PI * 0.22; // ≈40° tight arc

    const gfx = this.scene.add.graphics();
    gfx.setDepth(12);

    gfx.lineStyle(10, this.shieldColor, 0.35);
    gfx.beginPath();
    gfx.arc(cx, cy, r + 5, impactAngle - spread, impactAngle + spread, false);
    gfx.strokePath();

    gfx.lineStyle(4, this.shieldColor, 0.70);
    gfx.beginPath();
    gfx.arc(cx, cy, r, impactAngle - spread, impactAngle + spread, false);
    gfx.strokePath();

    gfx.lineStyle(1.5, 0xffffff, 0.90);
    gfx.beginPath();
    gfx.arc(cx, cy, r - 2, impactAngle - spread, impactAngle + spread, false);
    gfx.strokePath();

    this.scene.tweens.add({
      targets:    gfx,
      alpha:      0,
      duration:   350,
      ease:       'Power2',
      onComplete: () => gfx.destroy(),
    });
  }

  /**
   * Hull impact spark — fired when armor takes a direct hit.
   * impactAngle: same convention as onShieldHit (target→attacker direction).
   */
  onHullHit(impactAngle: number) {
    // Impact point on the hull surface
    const hx = this.image.x + Math.cos(impactAngle) * this.shipRadius * 0.85;
    const hy = this.image.y + Math.sin(impactAngle) * this.shipRadius * 0.85;

    // Central burst — positioned at hit point, scales outward
    const burst = this.scene.add.graphics();
    burst.setPosition(hx, hy);
    burst.setDepth(14);
    burst.fillStyle(0xffffff, 0.9);
    burst.fillCircle(0, 0, 4);
    burst.fillStyle(0xff8833, 0.85);
    burst.fillCircle(0, 0, 7);

    this.scene.tweens.add({
      targets:    burst,
      alpha:      0,
      scaleX:     2.5,
      scaleY:     2.5,
      duration:   200,
      ease:       'Power2',
      onComplete: () => burst.destroy(),
    });

    // 4–6 sparks scatter outward from the impact point
    const sparkCount = 4 + Math.floor(Math.random() * 3);
    for (let i = 0; i < sparkCount; i++) {
      const angle = impactAngle + (Math.random() - 0.5) * Math.PI * 0.9;
      const dist  = 12 + Math.random() * 18;
      const dur   = 120 + Math.random() * 100;

      const spark = this.scene.add.graphics();
      spark.setPosition(hx, hy);
      spark.setDepth(14);
      spark.fillStyle(0xff9900, 0.9);
      spark.fillRect(-2, -2, 4, 4);

      this.scene.tweens.add({
        targets:    spark,
        x:          hx + Math.cos(angle) * dist,
        y:          hy + Math.sin(angle) * dist,
        alpha:      0,
        duration:   dur,
        ease:       'Power1',
        onComplete: () => spark.destroy(),
      });
    }
  }

  /**
   * Death explosion: expanding rings, central flash, and scattered debris chunks.
   */
  private spawnExplosion() {
    const cx = this.image.x;
    const cy = this.image.y;
    const r  = this.shieldRadius;

    // ── Central white flash ─────────────────────────────────────────────────
    const flash = this.scene.add.graphics();
    flash.setPosition(cx, cy);
    flash.setDepth(20);
    flash.fillStyle(0xffffff, 1.0);
    flash.fillCircle(0, 0, r * 0.7);

    this.scene.tweens.add({
      targets:    flash,
      alpha:      0,
      scaleX:     2.2,
      scaleY:     2.2,
      duration:   350,
      ease:       'Power2',
      onComplete: () => flash.destroy(),
    });

    // ── Coloured inner fireball ─────────────────────────────────────────────
    const fireball = this.scene.add.graphics();
    fireball.setPosition(cx, cy);
    fireball.setDepth(19);
    fireball.fillStyle(this.shieldColor, 0.75);
    fireball.fillCircle(0, 0, r * 0.9);

    this.scene.tweens.add({
      targets:    fireball,
      alpha:      0,
      scaleX:     1.8,
      scaleY:     1.8,
      duration:   450,
      ease:       'Power2',
      onComplete: () => fireball.destroy(),
    });

    // ── Expanding outer ring ────────────────────────────────────────────────
    const ring = this.scene.add.graphics();
    ring.setPosition(cx, cy);
    ring.setDepth(18);
    ring.lineStyle(5, this.shieldColor, 0.7);
    ring.strokeCircle(0, 0, r);

    this.scene.tweens.add({
      targets:    ring,
      alpha:      0,
      scaleX:     3.5,
      scaleY:     3.5,
      duration:   600,
      ease:       'Power1',
      onComplete: () => ring.destroy(),
    });

    // ── Second, thinner shockwave ring ─────────────────────────────────────
    const shockwave = this.scene.add.graphics();
    shockwave.setPosition(cx, cy);
    shockwave.setDepth(17);
    shockwave.lineStyle(2, 0xffffff, 0.5);
    shockwave.strokeCircle(0, 0, r * 0.5);

    this.scene.tweens.add({
      targets:    shockwave,
      alpha:      0,
      scaleX:     5,
      scaleY:     5,
      duration:   800,
      ease:       'Power2',
      onComplete: () => shockwave.destroy(),
    });

    // ── Debris chunks ───────────────────────────────────────────────────────
    const debrisCount = 7 + Math.floor(Math.random() * 4); // 7–10 pieces
    for (let i = 0; i < debrisCount; i++) {
      const frame   = DEBRIS_FRAMES[Math.floor(Math.random() * DEBRIS_FRAMES.length)];
      const angle   = Math.random() * Math.PI * 2;
      const speed   = 60 + Math.random() * 160;
      const dur     = 900 + Math.random() * 700;
      const dist    = speed * (dur / 1000);

      const chunk = this.scene.add.image(cx, cy, 'sheet', frame);
      chunk.setDepth(15);
      chunk.setScale(0.4 + Math.random() * 0.5);
      chunk.setTint(this.shieldColor);

      this.scene.tweens.add({
        targets:   chunk,
        x:         cx + Math.cos(angle) * dist,
        y:         cy + Math.sin(angle) * dist,
        rotation:  (Math.random() - 0.5) * Math.PI * 6,
        alpha:     0,
        duration:  dur,
        ease:      'Power1',
        onComplete: () => chunk.destroy(),
      });
    }
  }

  update(delta: number) {
    if (this.isLocal) {
      if (this.predReady) {
        // Render directly from predicted state — no lerp lag
        this.image.x        = this.predX;
        this.image.y        = this.predY;
        this.image.rotation = this.predRot;
      } else {
        // Fallback before first server state — dead reckoning lerp
        const dt   = delta / 1000;
        const lerp = 1 - Math.exp(-LERP_K * dt);
        this.targetX += this.velX * dt;
        this.targetY += this.velY * dt;
        this.image.x = Phaser.Math.Linear(this.image.x, this.targetX, lerp);
        this.image.y = Phaser.Math.Linear(this.image.y, this.targetY, lerp);
        let diff = this.targetRotation - this.image.rotation;
        if (diff >  Math.PI) diff -= Math.PI * 2;
        if (diff < -Math.PI) diff += Math.PI * 2;
        this.image.rotation += diff * lerp;
      }
    } else {
      this.applyBufferedInterpolation();
    }

    this.flameTime += delta / 1000;

    // UFO nose indicator — pulsing cockpit dome + twin headlight beams
    // Wraith nose indicator — ghost aura: drifting green halo at nose tip
    if (this.noseLightGfx) {
      this.noseLightGfx.clear();
      if (!this.isDead) {
        const r    = this.image.rotation;
        const sinR = Math.sin(r), cosR = Math.cos(r);

        if (this.shipId === 'wraith') {
          const nx    = this.image.x + 26 * sinR;
          const ny    = this.image.y - 26 * cosR;
          const pulse = Math.sin(this.flameTime * 2.2) * 0.25 + 0.75;
          this.noseLightGfx.fillStyle(0x00ff88, 0.08 * pulse);
          this.noseLightGfx.fillCircle(nx, ny, 14);
          this.noseLightGfx.fillStyle(0x00ff88, 0.28 * pulse);
          this.noseLightGfx.fillCircle(nx, ny, 7);
          this.noseLightGfx.fillStyle(0xaaffdd, 0.80 * pulse);
          this.noseLightGfx.fillCircle(nx, ny, 3);
        } else {
          // robotai
          const nx    = this.image.x + 22 * sinR;
          const ny    = this.image.y - 22 * cosR;
          const pulse = Math.sin(this.flameTime * 3.5) * 0.20 + 0.80;

          this.noseLightGfx.fillStyle(0xff3728, 0.14 * pulse);
          this.noseLightGfx.fillCircle(nx, ny, 11);
          this.noseLightGfx.fillStyle(0xff3728, 0.46 * pulse);
          this.noseLightGfx.fillCircle(nx, ny, 6);
          this.noseLightGfx.fillStyle(0xffd2aa, 0.92 * pulse);
          this.noseLightGfx.fillCircle(nx, ny, 3.5);

          const fan = 0.245; // ~14 degrees
          this.noseLightGfx.lineStyle(1.5, 0xff3728, 0.13 * pulse);
          this.noseLightGfx.lineBetween(nx, ny,
            nx + Math.sin(r - fan) * 42, ny - Math.cos(r - fan) * 42);
          this.noseLightGfx.lineBetween(nx, ny,
            nx + Math.sin(r + fan) * 42, ny - Math.cos(r + fan) * 42);
        }
      }
    }

    // Enemy overlay — name + shield/armor bars above ship
    const showOverlay = uiState.showEnemyOverlay && !this.isLocal && !this.isDead;
    this.overlayGfx.clear();
    this.overlayName.setVisible(showOverlay);
    if (showOverlay) {
      const cx = this.image.x;
      const ty = this.image.y - 68;
      const bx = cx - 28;
      const bw = 56;
      this.overlayName.setPosition(cx, ty + 8);
      this.overlayGfx.fillStyle(0x00bbdd, 0.9);
      this.overlayGfx.fillRect(bx, ty + 18, bw * this.shieldPct, 4);
      this.overlayGfx.fillStyle(0x33bb55, 0.9);
      this.overlayGfx.fillRect(bx, ty + 25, bw * this.armorPct, 4);
    }

    // Persistent shield ring + thrust flames (same Graphics object, cleared together)
    this.shieldGfx.clear();

    // Thrust flames
    if (this.isThrustingNow && !this.isDead && this.nozzles.length > 0) {
      const r    = this.image.rotation;
      const cosR = Math.cos(r), sinR = Math.sin(r);
      for (const n of this.nozzles) {
        const bx = this.image.x + n.x * cosR - n.y * sinR;
        const by = this.image.y + n.x * sinR + n.y * cosR;
        const fx = -sinR, fy = cosR; // aft direction
        const rx = cosR,  ry = sinR; // right direction
        const x0 = bx - rx * n.halfW, y0 = by - ry * n.halfW;
        const x1 = bx + rx * n.halfW, y1 = by + ry * n.halfW;
        const tx = bx + fx * n.len,   ty = by + fy * n.len;
        this.shieldGfx.fillStyle(0xff6600, 0.35);
        this.shieldGfx.fillTriangle(x0 - rx, y0 - ry, x1 + rx, y1 + ry, tx + fx * 4, ty + fy * 4);
        this.shieldGfx.fillStyle(0xffdd55, 0.90);
        this.shieldGfx.fillTriangle(x0, y0, x1, y1, tx, ty);
        this.shieldGfx.fillStyle(0xffffff, 0.60);
        this.shieldGfx.fillTriangle(
          bx - rx * n.halfW * 0.35, by - ry * n.halfW * 0.35,
          bx + rx * n.halfW * 0.35, by + ry * n.halfW * 0.35,
          bx + fx * n.len * 0.55,   by + fy * n.len * 0.55,
        );
      }
    }

    if (this.shieldPct > 0 && !this.isDead) {
      const x     = this.image.x;
      const y     = this.image.y;
      const r     = this.shieldRadius;
      const alpha = 0.06 + (1 - this.shieldPct) * 0.08;

      this.shieldGfx.lineStyle(2, this.shieldColor, alpha + 0.06);
      this.shieldGfx.strokeCircle(x, y, r);

      this.shieldGfx.fillStyle(this.shieldColor, alpha * 0.25);
      this.shieldGfx.fillCircle(x, y, r);
    }
  }

  private applyBufferedInterpolation() {
    const renderTime = Date.now() - INTERP_DELAY_MS;
    const snaps = this.snapshots;
    if (snaps.length === 0) return;

    // Find the latest snapshot at or before renderTime
    let lo = -1;
    for (let i = 0; i < snaps.length; i++) {
      if (snaps[i].time <= renderTime) lo = i; else break;
    }

    if (lo === -1) {
      // Buffer hasn't filled yet — show at oldest known position
      this.image.x        = snaps[0].x;
      this.image.y        = snaps[0].y;
      this.image.rotation = snaps[0].rotation;
      return;
    }

    if (lo === snaps.length - 1) {
      // renderTime is ahead of all snapshots — dead reckon from newest
      const s  = snaps[lo];
      const dt = (renderTime - s.time) / 1000;
      this.image.x        = s.x + s.velX * dt;
      this.image.y        = s.y + s.velY * dt;
      this.image.rotation = s.rotation;
      return;
    }

    // Interpolate between snaps[lo] and snaps[lo+1]
    const a = snaps[lo];
    const b = snaps[lo + 1];
    const t = (renderTime - a.time) / (b.time - a.time);

    this.image.x = a.x + (b.x - a.x) * t;
    this.image.y = a.y + (b.y - a.y) * t;

    let rotDiff = b.rotation - a.rotation;
    if (rotDiff >  Math.PI) rotDiff -= Math.PI * 2;
    if (rotDiff < -Math.PI) rotDiff += Math.PI * 2;
    this.image.rotation = a.rotation + rotDiff * t;
  }

  setDead(dead: boolean) {
    this.isDead = dead;
    this.image.setVisible(!dead);
    this.shieldGfx.setVisible(!dead);
    if (dead) {
      this.noseLightGfx?.clear();
      this.spawnExplosion();
    }
  }

  destroy() {
    this.image.destroy();
    this.shieldGfx.destroy();
    this.noseLightGfx?.destroy();
    this.overlayGfx.destroy();
    this.overlayName.destroy();
  }
}
